import { useMemo } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useMediaQuery } from '@mui/material'
import { TRoomInformationProps } from './RoomInformation.types'
import { useLocation } from 'react-router-dom'
import { TPatientStatusOption } from 'lib/services/restApi/organizations/organizations.types'
import { EPatientStatus } from 'views/RoomView/RoomView.types'
import { useUser } from 'app/User'
import dayjs from 'lib/dayjs'

export const useRoomInformationLogic = (props: TRoomInformationProps) => {
  const { surgery } = props
  const flags = useFlags()
  const isCancelled = surgery.status?.name === 'CANCELLED'
  const isSmallScreen = useMediaQuery('(max-width:640px)')
  const { isNurse, isNurseAdmin } = useUser()
  const isNurseOrNurseAdmin = isNurse || isNurseAdmin

  const cursor =
    useLocation().pathname === '/surgeries' &&
    flags.enablePatientStatus &&
    isNurseOrNurseAdmin &&
    !isCancelled
      ? 'pointer'
      : 'default'

  const room: string | undefined = flags.enablePatientStatus
    ? surgery.patientStatusHistory?.[0]?.info?.room?.name
    : surgery.visit?.location?.room || ''

  const isManuallyAdded = surgery.isManuallyAdded

  const earliestDateTime = dayjs(
    surgery.procedures
      ?.map((proc) => proc.dateTime)
      .filter(Boolean)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0]
  ).toDate()

  const timeString = earliestDateTime
    ? dayjs(earliestDateTime)?.format('h:mm A') || ''
    : ''

  const roomNumberStyle = useMemo(() => {
    if (!room) return {}

    // Base font sizes for different room lengths
    let baseFontSize: string

    if (room.length <= 5) {
      switch (room.length) {
        case 1:
          baseFontSize = '2.4rem'
          break
        case 2:
          baseFontSize = '2.2rem'
          break
        case 3:
          baseFontSize = '1.9rem'
          break
        case 4:
          baseFontSize = '1.6rem'
          break
        case 5:
          baseFontSize = '1.4rem'
          break
        default:
          baseFontSize = '1.4rem'
      }
    } else {
      // Handle longer room numbers
      if (room.length >= 10) baseFontSize = '0.6rem'
      else if (room.length >= 8) baseFontSize = '0.8rem'
      else if (room.length >= 6) baseFontSize = '1.2rem'
      else baseFontSize = '1.4rem'
    }

    // Adjust font sizes for small screens (smaller than 640px)
    if (isSmallScreen) {
      const baseSize = parseFloat(baseFontSize)
      const smallScreenSize = `${(baseSize * 0.8).toFixed(1)}rem`
      return { fontSize: smallScreenSize }
    }

    return { fontSize: baseFontSize }
  }, [room, isSmallScreen])

  const patientStatusOptions = JSON.parse(
    localStorage.getItem('patientStatusOptions') || '[]'
  )

  const status =
    surgery.status?.name === 'CANCELLED'
      ? 'CANCELLED'
      : surgery.patientStatusHistory?.[0]?.status
      ? surgery.patientStatusHistory?.[0]?.status === EPatientStatus.ASSIGN_ROOM
        ? patientStatusOptions.find(
            (option: TPatientStatusOption) =>
              option.status === surgery.patientStatusHistory?.[0]?.status
          )?.displayText
        : patientStatusOptions.find(
            (option: TPatientStatusOption) =>
              option.status === surgery.patientStatusHistory?.[0]?.status
          )?.value
      : patientStatusOptions?.[0]?.value

  const statusColor =
    surgery.status?.name === 'CANCELLED'
      ? 'var(--error-contrast-text)'
      : flags.enablePatientStatus
      ? surgery.patientStatusHistory?.[0]?.status
        ? patientStatusOptions.find(
            (option: TPatientStatusOption) =>
              option.status === surgery.patientStatusHistory?.[0]?.status
          )?.color
        : patientStatusOptions?.[0]?.color
      : 'var(--primary)'

  return {
    room,
    flags,
    status,
    cursor,
    timeString,
    statusColor,
    isManuallyAdded,
    roomNumberStyle,
    earliestDateTime,
    showSurgeryTime: flags.showSurgeryTime,
    isCancelled,
    isNurseOrNurseAdmin,
  }
}
